import { RbacPage, RbacPageName } from 'app/core/rbac/rbac-page-names.const';
import { scoreKeywordList } from './slate-nlp.pure';

export interface SlateGuideEntry {
    id: string;
    title: string;
    keywords: string[];
    audience: 'admin' | 'filmmaker' | 'both';
    rbacPage?: RbacPageName;
    steps: string[];
    route: string;
    /** Settings sidebar tab deep-link */
    queryParams?: Record<string, string>;
    linkLabel: string;
    relatedPrompts?: string[];
    /** Shown when user asks but role does not match this guide */
    roleNote?: string;
    /** Score multiplier — specific how-tos beat generic pages */
    weight?: number;
}

export const SLATE_ROUTE_GUIDES: SlateGuideEntry[] = [
    {
        id: 'home_page_settings',
        title: "Home Page settings (slider & banners)",
        audience: 'admin',
        rbacPage: RbacPage.HOME_PAGE_SETTINGS,
        weight: 2.2,
        keywords: [
            "home page",
            "homepage",
            "home page settings",
            "homepage settings",
            "home page film",
            "home page films",
            "home page listing",
            "home page film listing",
            "update home page",
            "edit home page",
            "change home page",
            "manage home page",
            "home screen",
            "consumer home",
            "app home page",
            "website home",
        ],
        steps: [
            "Open Settings → Home Page (not Box Office → Films).",
            "Three tabs control consumer home content: Slider Films, Recommended Films, Static Banner.",
            "Slider = hero carousel. Recommended = recommended row. Static Banner = promo banners with URLs/images.",
            "Add films from the dropdown, drag to reorder, remove if needed.",
            "Needs RBAC on Home-Page-Settings plus the tab (Slider-Films / Recommended-Films / Static-Banner). Write=add, Update=reorder, Delete=remove.",
            "Filmmaker / user levels are NOT here — Settings → Filmmakers → Filmmaker Level, or Settings → Users → User Level.",
        ],
        route: '/admin/settings/home-page',
        queryParams: { tab: 'slider-films' },
        linkLabel: "Open Home Page → Slider",
        relatedPrompts: [
            "How add film to slider?",
            "How manage recommended films?",
            "How add static banner?",
            "Where are filmmaker levels?",
        ],
    },
    {
        id: 'home_page_slider',
        title: "Add / reorder Home Page slider films",
        audience: 'admin',
        rbacPage: RbacPage.SETTINGS_SLIDER_FILMS,
        weight: 2.4,
        keywords: [
            "slider",
            "slider films",
            "home page slider",
            "homepage slider",
            "hero slider",
            "carousel",
            "add to slider",
            "add film to slider",
            "film to slider",
            "slider listing",
            "promoted films",
        ],
        steps: [
            "Open Settings → Home Page → Slider Films.",
            "Select a film from Select Slider Films, then Add. Duplicate shows Film already exists in the slider.",
            "Under Selected Slider Films, drag-and-drop to set order (Update permission).",
            "Remove with trash (Delete permission). Order drives the consumer app carousel.",
        ],
        route: '/admin/settings/home-page',
        queryParams: { tab: 'slider-films' },
        linkLabel: "Open Slider Films",
        relatedPrompts: [
            "How manage recommended films?",
            "How add static banner?",
            "Home page settings",
        ],
    },
    {
        id: 'home_page_recommended',
        title: "Manage Recommended Films on Home Page",
        audience: 'admin',
        rbacPage: RbacPage.SETTINGS_RECOMMENDED_FILMS,
        weight: 2.2,
        keywords: [
            "recommended films",
            "recommended film",
            "home page recommended",
            "recommend films",
            "recommended listing",
        ],
        steps: [
            "Open Settings → Home Page → Recommended Films.",
            "Same pattern as slider: pick film → Add → drag reorder → remove.",
            "Feeds the recommended row on consumer home — separate from Slider Films.",
        ],
        route: '/admin/settings/home-page',
        queryParams: { tab: 'recommended-films' },
        linkLabel: "Open Recommended Films",
        relatedPrompts: [
            "How add film to slider?",
            "How add static banner?",
        ],
    },
    {
        id: 'home_page_banner',
        title: "Add / edit Static Banner",
        audience: 'admin',
        rbacPage: RbacPage.SETTINGS_STATIC_BANNER,
        weight: 2.1,
        keywords: [
            "static banner",
            "home page banner",
            "banner settings",
            "promo banner",
            "add banner",
            "edit banner",
        ],
        steps: [
            "Open Settings → Home Page → Static Banner.",
            "Add: Banner Name, Web redirection URL, optional mobile banner + URL, images (JPG/PNG max 5MB), publish + scheduler dates.",
            "Drag to reorder. Edit or delete with Update/Delete permission.",
        ],
        route: '/admin/settings/home-page',
        queryParams: { tab: 'static-banner' },
        linkLabel: "Open Static Banner",
        relatedPrompts: [
            "Home page settings",
            "How add film to slider?",
        ],
    },
    {
        id: 'filmmaker_level_settings',
        title: "Filmmaker Level settings",
        audience: 'admin',
        rbacPage: RbacPage.SETTINGS_FILMMAKER_LEVEL,
        weight: 2.2,
        keywords: [
            "filmmaker level",
            "filmmaker levels",
            "film maker level",
            "home page level",
            "homepage level",
            "level options",
            "filmmaker tier",
            "initial reels",
            "sync levels",
        ],
        steps: [
            "Levels live under Settings → Filmmakers → Filmmaker Level — not Home Page.",
            "Basic Configuration: Unit Type, Initial Reels.",
            "Filmmaker Levels: add / insert / remove. Per level: name, min/max, campaign, status.",
            "Preview Changes → Save → Sync Levels (1–2 min). Always save before sync. Watch overlap validation.",
        ],
        route: '/admin/settings/filmmakers',
        queryParams: { tab: 'filmmaker-level' },
        linkLabel: "Open Filmmaker Level",
        relatedPrompts: [
            "Where are user levels?",
            "Upload content settings",
            "Home page settings",
        ],
    },
    {
        id: 'user_level_settings',
        title: "User / viewer Level settings",
        audience: 'admin',
        rbacPage: RbacPage.SETTINGS_USER_LEVEL,
        weight: 2.1,
        keywords: [
            "user level",
            "user levels",
            "viewer level",
            "viewer levels",
            "audience level",
            "coins level",
        ],
        steps: [
            "Open Settings → Users → User Level.",
            "Basic Configuration: Unit Type (Cash or Coins), Time Period Type (period vs lifetime), Period Days, Start Date, Include Promotional.",
            "Manage level rows. Preview → Save → Sync Levels.",
        ],
        route: '/admin/settings/users',
        queryParams: { tab: 'user-level' },
        linkLabel: "Open User Level",
        relatedPrompts: [
            "Where are filmmaker levels?",
            "Rewards settings",
        ],
    },
    {
        id: 'upload_content_settings',
        title: "Upload Content settings (fees / assets)",
        audience: 'admin',
        rbacPage: RbacPage.SETTINGS_UPLOAD_CONTENT,
        weight: 1.8,
        keywords: [
            "upload content settings",
            "upload settings",
            "content upload settings",
            "upload fee",
            "submission fee settings",
        ],
        steps: [
            "Open Settings → Filmmakers → Upload Content.",
            "Enable toggle, Unit Type, manage Upload Content Assets (type, unit value, active).",
            "Assets apply to Films / Web Series. Save when Update permission allows.",
        ],
        route: '/admin/settings/filmmakers',
        queryParams: { tab: 'upload-content' },
        linkLabel: "Open Upload Content Settings",
        relatedPrompts: [
            "How do I upload a film?",
            "Where are filmmaker levels?",
        ],
    },

    {
        id: 'upload_film_fm',
        title: 'Upload a film (Filmmaker)',
        audience: 'filmmaker',
        keywords: [
            'upload film',
            'upload a film',
            'upload movie',
            'how to upload',
            'how do i upload',
            'how upload',
            'media upload',
            'submit film',
            'publish film',
            'add film',
            'create film',
            'upload content',
        ],
        steps: [
            'Open Media Upload from the left menu (or use the button below).',
            'Choose content type (Film) and fill required fields: title, language, genre, age group.',
            'Upload video file and posters in the media steps. Wait for processing to finish.',
            'Set pricing / ticket fields, then Submit — or Save as Draft anytime.',
            'Track status under My Films. If stuck, check wallet/reel balance and upload settings.',
        ],
        route: '/film-maker/media-upload',
        linkLabel: 'Open Media Upload',
        relatedPrompts: ['Show my films', 'My film views last 7 days', 'Wallet balance'],
        roleNote: 'Media Upload is available on the Filmmaker portal.',
    },
    {
        id: 'upload_film_admin',
        title: 'Film upload & review (Admin)',
        audience: 'admin',
        rbacPage: RbacPage.FILM_LIST,
        keywords: [
            'upload film',
            'upload a film',
            'upload movie',
            'how to upload',
            'how do i upload',
            'how upload',
            'media upload',
            'submit film',
            'publish film',


            'upload content',
            'approve film',
            'review film',
        ],
        steps: [
            'New film uploads come from Filmmakers via Media Upload (Filmmaker portal) — not from Admin create-upload.',
            'As Admin, open Box Office → Films to search, filter (status / genre / language / dates), and open a title.',
            'Use Drafted Films for unfinished filmmaker drafts that still need work.',
            'On film detail/edit: review media, metadata, pricing, then Approve / Reject / Disable based on your permissions.',
            'Upload limits & pricing rules live under Settings → Filmmakers / Financial. RBAC controls who can edit.',
            'If a filmmaker cannot upload: check their FILM_MAKER role, content-upload settings, and any 403 on Media Upload.',
            'Consumer home carousel listing is Settings → Home Page — not Box Office.',
        ],
        route: '/admin/box-office/films',
        linkLabel: 'Open Films (Box Office)',
        relatedPrompts: [
            'List films from last 30 days',
            'How many films pending?',
            'Where is drafted films?',
        ],
        roleNote: 'Admins review films in Box Office; filmmakers upload via Media Upload.',
    },
    {
        id: 'find_dashboard_admin',
        title: 'Admin dashboard',
        audience: 'admin',
        rbacPage: RbacPage.DASHBOARD,
        keywords: [
            'dashboard',
            'where is dashboard',
            'open dashboard',
            'analytics dashboard',
            'film dashboard',
            'how to use dashboard',
        ],
        steps: [
            'Open Dashboard from the main menu.',
            'Tabs: Film, Web Series, Users, Rewards (only tabs your RBAC allows).',
            'Set date range (Last 7 days = today minus 7 through today), optional filmmaker/film filters, Group By.',
            'Read Views / Volume / Revenue tiles, charts, and Top films table.',
            'Ask Talkie for the same live metrics, e.g. "Film views on 10th August 2026".',
        ],
        route: '/admin/dashboard',
        linkLabel: 'Open Dashboard',
        relatedPrompts: [
            'Film views last 7 days',
            'What are the views on 10th August 2026?',
        ],
    },
    {
        id: 'find_dashboard_fm',
        title: 'Filmmaker dashboard',
        audience: 'filmmaker',
        keywords: [
            'dashboard',
            'where is dashboard',
            'open dashboard',
            'my analytics',
            'my performance',
            'how to use dashboard',
        ],
        steps: [
            'Open Dashboard in the filmmaker menu.',
            'Pick a date range and optional film filter.',
            'Review views, purchases, minutes watched, and top titles for your content only.',
        ],
        route: '/film-maker/dashboard',
        linkLabel: 'Open My Dashboard',
        relatedPrompts: ['My film views last 7 days', 'Show my films'],
    },
    {
        id: 'open_views_report',
        title: 'Views report',
        audience: 'admin',
        rbacPage: RbacPage.VIEWS_REPORT,
        keywords: [
            'views report',
            'where is views report',
            'film views report',
            'open views report',
        ],
        steps: [
            'Open Reports → Views Report.',
            'Set start/end dates (required for meaningful results).',
            'Use search/sort on the table; export/download only if your role has download permission.',
        ],
        route: '/admin/reports/views-report',
        linkLabel: 'Open Views Report',
        relatedPrompts: ['Views report last month', 'Film views last 7 days'],
    },
    {
        id: 'open_watch_activity',
        title: 'Watch activity report',
        audience: 'admin',
        rbacPage: RbacPage.WATCH_ACTIVITY_REPORT,
        keywords: [
            'watch activity',
            'where is watch activity',
            'watch activity report',
        ],
        steps: [
            'Open Reports → Watch Activity Report.',
            'Filter by date range and content type / access type.',
            'Inspect session-level watch rows in the table.',
        ],
        route: '/admin/reports/watch-activity-report',
        linkLabel: 'Open Watch Activity',
        relatedPrompts: ['Watch activity last week'],
    },
    {
        id: 'open_purchase_report',
        title: 'Content purchase report',
        audience: 'admin',
        rbacPage: RbacPage.PURCHASE_REPORT,
        keywords: [
            'purchase report',
            'where is purchase report',
            'content purchase',
            'sales report page',
        ],
        steps: [
            'Open Reports → Film Purchase Report.',
            'Choose start and end dates.',
            'Inspect purchase rows; export when download permission allows.',
        ],
        route: '/admin/reports/film-purchase-report',
        linkLabel: 'Open Purchase Report',
        relatedPrompts: ['Content purchase report last 30 days'],
    },
    {
        id: 'film_list_admin',
        title: 'Find & manage films',
        audience: 'admin',
        rbacPage: RbacPage.FILM_LIST,
        keywords: [
            'film list',
            'where are films',
            'box office films',
            'find films',
            'manage films',
            'edit film',
            'film status',
        ],
        steps: [
            'Open Box Office → Films.',
            'Filters: status, genre, language, date range, search (title).',
            'Open a row to view/edit steps (basic info, media, price) — fields depend on your RBAC write/update bits.',
            'Drafted Films is a separate page for unfinished uploads.',
        ],
        route: '/admin/box-office/films',
        linkLabel: 'Open Films',
        relatedPrompts: [
            'List films from last 30 days',
            'How many films pending?',
        ],
    },
    {
        id: 'drafted_films',
        title: 'Drafted films',
        audience: 'admin',
        rbacPage: RbacPage.DRAFTED_FILMS,
        keywords: [
            'drafted films',
            'draft films',
            'incomplete films',
            'where is drafted',
        ],
        steps: [
            'Open Box Office → Drafted Films.',
            'Search or filter drafts that need review.',
            'Open a draft to continue editing (same stepper fields as film edit).',
        ],
        route: '/admin/box-office/drafted-films',
        linkLabel: 'Open Drafted Films',
    },
    {
        id: 'my_films',
        title: 'My films',
        audience: 'filmmaker',
        keywords: ['my films', 'where are my films', 'film list', 'my titles'],
        steps: [
            'Open My Films from the left menu.',
            'Check status for each title (draft / pending / approved / rejected).',
            'Open a film to view details or continue edits on allowed fields.',
        ],
        route: '/film-maker/my-films',
        linkLabel: 'Open My Films',
        relatedPrompts: ['Show my films', 'How do I upload a film?'],
    },
    {
        id: 'wallet',
        title: 'Wallet & transactions',
        audience: 'filmmaker',
        keywords: [
            'where is wallet',
            'where is my wallet',
            'open transactions',
            'transaction history page',
            'where is transaction history',
        ],
        steps: [
            'Cash and Reel balances show in the top header.',
            'Open Transaction History for the full ledger.',
            'Filter by balance type (cash / reel) when needed.',
        ],
        route: '/film-maker/transaction-history',
        linkLabel: 'Open Transactions',
        relatedPrompts: ['Wallet balance', 'Show my films'],
    },
    {
        id: 'settings_financial',
        title: 'Pricing & financial settings',
        audience: 'admin',
        rbacPage: RbacPage.FINANCIAL_SETTINGS,
        keywords: [
            'pricing settings',
            'financial settings',
            'how change pricing',
            'ticket price settings',
        ],
        steps: [
            'Open Settings → Financial.',
            'Review pricing / currency / related limits carefully.',
            'Save only when you have update permission on that settings page.',
        ],
        route: '/admin/settings/financial',
        linkLabel: 'Open Financial Settings',
    },
    {
        id: 'settings_rewards',
        title: 'Rewards settings',
        audience: 'admin',
        rbacPage: RbacPage.REWARD_SETTINGS,
        keywords: [
            'rewards settings',
            'how change rewards',
            'cashback settings',
            'promotional settings',
        ],
        steps: [
            'Open Settings → Rewards.',
            'Adjust reward / cashback configuration as permitted.',
            'Confirm changes before saving.',
        ],
        route: '/admin/settings/rewards',
        linkLabel: 'Open Rewards Settings',
    },
    {
        id: 'contest',
        title: 'Big Short Challenge',
        audience: 'admin',
        rbacPage: RbacPage.BIG_SHORT_DASHBOARD,
        keywords: [
            'big short',
            'tbsc',
            'contest',
            'challenge dashboard',
            'film contest',
        ],
        steps: [
            'Open Big Short Challenge from the menu.',
            'Pick a contest to view dashboard metrics.',
            'Drill into registered users, uploads, and payments.',
        ],
        route: '/admin/big-short-challenge/dashboard',
        linkLabel: 'Open Big Short Challenge',
    },
    {
        id: 'rbac_help',
        title: 'Permissions & roles help',
        audience: 'admin',
        keywords: [
            "why can't i",
            'no permission',
            'access denied',
            'permissions',
            'rbac',
            'access control',
            'my role',
            'what can i access',
        ],
        steps: [
            'Every Studio page is gated by RBAC: role + page + action (read/write/update/delete).',
            'Missing menu items or 403 responses usually mean your role lacks that page read.',
            'Super Admin bypasses RBAC. Other admin roles only see granted pages.',
            'Filmmakers use a separate portal and never call admin APIs.',
            'Ask a Super Admin to grant pages in Access Control if you need more access.',
        ],
        route: '/admin/settings/access-control',
        linkLabel: 'Open Access Control',
        relatedPrompts: ['Where is dashboard?', 'How do I upload a film?'],
    },
        {
        id: 'upload_series_fm',
        title: "Upload a web series (Filmmaker)",
        audience: 'filmmaker',
        weight: 1.7,
        keywords: [
            "upload series",
            "upload web series",
            "upload webseries",
            "new series",
            "add series",
            "upload season",
            "upload episode",
        ],
        steps: [
            "Open Media Upload → choose Webseries (or Micro Series).",
            "Flow: Series Details → Season Details → Episodes → Pricing.",
            "After submit, manage titles under My Series.",
        ],
        route: '/film-maker/media-upload',
        linkLabel: "Open Media Upload",
        relatedPrompts: [
            "Show my series",
            "How do I upload a film?",
        ],
    },
    {
        id: 'open_wallet_report',
        title: "Wallet report",
        audience: 'admin',
        rbacPage: RbacPage.WALLET_REPORT,
        weight: 1.5,
        keywords: [
            "wallet report",
            "where is wallet report",
            "wallet transactions report",
        ],
        steps: [
            "Open Reports → Wallet Report.",
            "Find a user row, then open drill-down for that user's wallet transactions.",
        ],
        route: '/admin/reports/wallet-report',
        linkLabel: "Open Wallet Report",
        relatedPrompts: [
            "Add transaction",
        ],
    },
    {
        id: 'open_analytics_report',
        title: "Analytics report",
        audience: 'admin',
        rbacPage: RbacPage.ANALYTICS_REPORT,
        keywords: [
            "analytics report",
            "where is analytics report",
        ],
        steps: [
            "Open Reports → Analytics Report.",
            "Apply date filters and review aggregated analytics rows / export if permitted.",
        ],
        route: '/admin/reports/analytics-report',
        linkLabel: "Open Analytics Report",
    },
    {
        id: 'open_filmmaker_report',
        title: "Filmmaker report",
        audience: 'admin',
        rbacPage: RbacPage.FILMMAKER_REPORT,
        keywords: [
            "filmmaker report",
            "film maker report",
        ],
        steps: [
            "Open Reports → Filmmaker Report.",
            "Filter dates and inspect per-filmmaker performance rows.",
        ],
        route: '/admin/reports/filmmaker-report',
        linkLabel: "Open Filmmaker Report",
    },
    {
        id: 'webseries_list_admin',
        title: "Manage web series (Box Office)",
        audience: 'admin',
        rbacPage: RbacPage.WEB_SERIES_LIST,
        weight: 1.4,
        keywords: [
            "web series list",
            "manage series",
            "box office series",
            "where are series",
            "approve series",
        ],
        steps: [
            "Open Box Office → Series.",
            "Filter and open a series to review seasons/episodes/pricing.",
            "Approve / Decline / Suspend / Convert similar to films, based on permissions.",
        ],
        route: '/admin/box-office/webseries',
        linkLabel: "Open Series",
        relatedPrompts: [
            "List web series",
            "Web series analytics this week",
        ],
    },
    {
        id: 'my_series',
        title: "My series",
        audience: 'filmmaker',
        keywords: [
            "my series",
            "where are my series",
            "my web series",
            "manage my series",
        ],
        steps: [
            "Open My Series.",
            "Review stats tiles and series table. Open a row for seasons/episodes.",
            "Upload New Series via Media Upload when needed.",
        ],
        route: '/film-maker/my-series',
        linkLabel: "Open My Series",
        relatedPrompts: [
            "How upload series?",
            "Show my films",
        ],
    },
    {
        id: 'users_filmmakers',
        title: "Filmmaker users",
        audience: 'admin',
        rbacPage: RbacPage.FILMMAKERS,
        weight: 1.4,
        keywords: [
            "filmmaker list",
            "list filmmakers",
            "where are filmmakers",
            "filmmaker profile",
            "filmmaker kyc",
        ],
        steps: [
            "Open Users → Filmmakers.",
            "Open a filmmaker for tabs: Basic, Production House, KYC, Banking, Films, Transactions.",
        ],
        route: '/admin/users/film-makers',
        linkLabel: "Open Filmmakers",
        relatedPrompts: [
            "List filmmakers",
            "Filmmaker report",
        ],
    },
    {
        id: 'users_viewers',
        title: "Viewer users",
        audience: 'admin',
        rbacPage: RbacPage.VIEWERS,
        keywords: [
            "viewer list",
            "list viewers",
            "where are viewers",
            "viewer profile",
        ],
        steps: [
            "Open Users → Viewers.",
            "Open a viewer for Basic, Purchase History, Transactions.",
        ],
        route: '/admin/users/viewers',
        linkLabel: "Open Viewers",
    },
    {
        id: 'referral_fm',
        title: "Referral & Sharing",
        audience: 'filmmaker',
        keywords: [
            "referral history",
            "referral sharing",
            "sharing history",
            "where is referral",
            "open referral",
        ],
        steps: [
            "Open Referral & Sharing.",
            "Tabs: Sharing History and Referral History.",
        ],
        route: '/film-maker/referral-sharing',
        linkLabel: "Open Referral & Sharing",
    },
    {
        id: 'add_transaction',
        title: "Add manual transaction",
        audience: 'admin',
        rbacPage: RbacPage.ADD_TRANSACTION,
        weight: 1.5,
        keywords: [
            "add transaction",
            "manual transaction",
            "credit wallet",
            "debit wallet",
        ],
        steps: [
            "Open Financial → Add Transaction.",
            "Select user/context and complete the form per your finance policy.",
            "Confirm before submit — this changes wallet balances.",
        ],
        route: '/admin/financial/add-transaction',
        linkLabel: "Open Add Transaction",
    },
    {
        id: 'settings_withdrawal',
        title: "Withdrawal settings",
        audience: 'admin',
        rbacPage: RbacPage.SETTINGS_WITHDRAWAL,
        weight: 1.8,
        keywords: [
            "withdrawal settings",
            "enable withdrawal",
            "withdrawal window",
            "auto approve withdrawal",
        ],
        steps: [
            "Open Settings → Financial → Withdrawal.",
            "Master Enable Withdrawals switch, window days, amount limits, auto-approve below, processing fee, eligibility, payment methods.",
            "Save carefully — affects filmmaker payouts.",
        ],
        route: '/admin/settings/financial',
        queryParams: { tab: 'withdrawal' },
        linkLabel: "Open Withdrawal Settings",
    },
    {
        id: 'genres_languages',
        title: "Genres, languages & age groups",
        audience: 'admin',
        weight: 1.3,
        keywords: [
            "genres",
            "add genre",
            "languages",
            "age groups",
            "master data",
        ],
        steps: [
            "Top menu: Genres, Languages, Age Groups.",
            "Use form routes to add/edit entries used by film upload metadata.",
        ],
        route: '/admin/genres',
        linkLabel: "Open Genres",
        relatedPrompts: [
            "How do I upload a film?",
        ],
    },
    {
        id: 'profile_fm',
        title: "My profile",
        audience: 'filmmaker',
        keywords: [
            "my profile",
            "edit profile",
            "where is profile",
        ],
        steps: [
            "Open My Profile from the filmmaker menu (or /user/profile).",
            "Update personal / production details allowed on the form.",
        ],
        route: '/user/profile',
        linkLabel: "Open My Profile",
    },
    {
        id: 'open_invoice_report',
        title: "Invoice Report",
        audience: 'admin',
        rbacPage: RbacPage.INVOICE_REPORT,
        weight: 1.6,
        keywords: [
            "invoice report",
            "where is invoice",
            "purchase invoice",
            "billing report",
            "content purchase invoice",
        ],
        steps: [
            "Open Reports → Invoice Report.",
            "Set date range for the invoice window.",
            "Review content, buyer, filmmaker, and payment-split columns; expand a row for detail.",
            "Download CSV when your role has download permission.",
        ],
        route: '/admin/reports/invoice-report',
        linkLabel: "Open Invoice Report",
        relatedPrompts: [
            "Content purchase report last 30 days",
            "Views report last month",
        ],
    },
    {
        id: 'open_promotional_report',
        title: "Promotional Report",
        audience: 'admin',
        rbacPage: RbacPage.PROMOTIONAL_REPORT,
        weight: 1.6,
        keywords: [
            "promotional report",
            "promo purchases",
            "promotional transactions",
            "where promotional report",
        ],
        steps: [
            "Open Reports → Promotional Report.",
            "Filter by purchase date range.",
            "Inspect promo purchase rows (content, buyer, wallet/reward/gateway splits).",
            "Export when download is allowed.",
        ],
        route: '/admin/reports/promotional-report',
        linkLabel: "Open Promotional Report",
    },
    {
        id: 'open_free_content_report',
        title: "Free Content Report",
        audience: 'admin',
        rbacPage: RbacPage.FREE_CONTENT_REPORT,
        weight: 1.6,
        keywords: [
            "free content report",
            "free watch report",
            "free views report",
            "where free content",
        ],
        steps: [
            "Open Reports → Free Content Report.",
            "Set the date range for free-content activity.",
            "Inspect title, type, views, unique views, minutes watched.",
            "Export if permitted.",
        ],
        route: '/admin/reports/free-content-report',
        linkLabel: "Open Free Content Report",
    },
    {
        id: 'open_film_report',
        title: "Film Report",
        audience: 'admin',
        rbacPage: RbacPage.FILM_REPORT,
        weight: 1.5,
        keywords: [
            "film report",
            "movie catalog report",
            "ticket price report",
            "where film report",
        ],
        steps: [
            "Open Reports → Film Report.",
            "Filter/sort by created or approved date.",
            "Review title, filmmaker, ticket price, duration, partner fields.",
            "Download for finance/ops review.",
        ],
        route: '/admin/reports/film-report',
        linkLabel: "Open Film Report",
    },
    {
        id: 'open_viewer_report',
        title: "Viewer Report",
        audience: 'admin',
        rbacPage: RbacPage.VIEWER_REPORT,
        weight: 1.5,
        keywords: [
            "viewer report",
            "audience report",
            "registered viewers",
            "where viewer report",
        ],
        steps: [
            "Open Reports → Viewer Report.",
            "Set registration / activity date range.",
            "Browse name, email, phone, last purchased date.",
            "Export when permitted.",
        ],
        route: '/admin/reports/viewer-report',
        linkLabel: "Open Viewer Report",
    },
    {
        id: 'open_cc_payment_report',
        title: "CC Payment Report",
        audience: 'admin',
        rbacPage: RbacPage.CC_PAYMENT_REPORT,
        weight: 1.6,
        keywords: [
            "cc payment report",
            "channel partner payment",
            "cp commission report",
            "where cc payment",
        ],
        steps: [
            "Open Reports → CC Payment Report.",
            "Filter by date range.",
            "Open row detail for partner KYC/banking fields.",
            "Export for reconciliation.",
        ],
        route: '/admin/reports/cc-payment-report',
        linkLabel: "Open CC Payment Report",
    },
    {
        id: 'open_payment_gateway_report',
        title: "Payment Gateway Report",
        audience: 'admin',
        rbacPage: RbacPage.PAYMENT_GATEWAY_REPORT,
        weight: 1.6,
        keywords: [
            "payment gateway report",
            "cashfree report",
            "razorpay report",
            "gateway transactions",
            "where cashfree",
        ],
        steps: [
            "Open Reports → Payment Gateway Report.",
            "Pick gateway type: All / Cashfree / Razorpay / iOS.",
            "Set transaction date range; review amount, order ID, customer.",
            "Download filtered export.",
        ],
        route: '/admin/reports/cashfree-report',
        linkLabel: "Open Payment Gateway Report",
    },
    {
        id: 'open_iap_report',
        title: "In-App Purchase Report",
        audience: 'admin',
        rbacPage: RbacPage.IN_APP_PURCHASE_REPORT,
        weight: 1.6,
        keywords: [
            "in app purchase report",
            "iap report",
            "mobile purchase report",
            "app store purchases",
        ],
        steps: [
            "Open Reports → In App Purchase Report.",
            "Filter by transaction date.",
            "Review customer, amount, payment order ID, phone.",
            "Export if allowed.",
        ],
        route: '/admin/reports/in-app-purchase-report',
        linkLabel: "Open IAP Report",
    },
    {
        id: 'financial_filmmaker_list',
        title: "Filmmaker financial list",
        audience: 'admin',
        rbacPage: RbacPage.FILMMAKER_FINANCIAL_LIST,
        weight: 1.5,
        keywords: [
            "filmmaker financial",
            "filmmaker finance list",
            "where filmmaker financial",
            "filmmaker balances",
        ],
        steps: [
            "Open Financial → Financial Filmmaker.",
            "Search/filter filmmakers and open a row for balances and history.",
            "Drill into /admin/financial/filmmaker/:userId for detail.",
        ],
        route: '/admin/financial/filmmaker',
        linkLabel: "Open Filmmaker Financial",
    },
    {
        id: 'financial_viewer_list',
        title: "Viewer financial list",
        audience: 'admin',
        rbacPage: RbacPage.VIEWER_FINANCIAL_LIST,
        weight: 1.5,
        keywords: [
            "viewer financial",
            "viewer finance list",
            "where viewer financial",
            "viewer balances",
        ],
        steps: [
            "Open Financial → Financial Viewer.",
            "Search viewers and open a row for wallet/finance detail.",
            "Drill into /admin/financial/viewer/:userId when needed.",
        ],
        route: '/admin/financial/viewer',
        linkLabel: "Open Viewer Financial",
    },
    {
        id: 'users_admins',
        title: "Admin users",
        audience: 'admin',
        rbacPage: RbacPage.ADMINS,
        weight: 1.4,
        keywords: [
            "admin list",
            "list admins",
            "where are admins",
            "create admin user",
        ],
        steps: [
            "Open Users → Admins.",
            "Review admin accounts; use Create User for new staff when permitted.",
            "Open a row to view/edit admin profile.",
        ],
        route: '/admin/users/admin-list',
        linkLabel: "Open Admins",
    },
    {
        id: 'users_channel_partners',
        title: "Channel Partners",
        audience: 'admin',
        rbacPage: RbacPage.CHANNEL_PARTNER,
        weight: 1.4,
        keywords: [
            "channel partners",
            "channel partner list",
            "where are channel partners",
            "create channel partner",
        ],
        steps: [
            "Open Users → Channel Partners.",
            "Create via Create CP when needed.",
            "Open a partner profile for view/edit.",
        ],
        route: '/admin/users/channel-partners',
        linkLabel: "Open Channel Partners",
    },
    {
        id: 'users_content_curators',
        title: "Content Curators",
        audience: 'admin',
        rbacPage: RbacPage.CONTENT_CURATOR,
        weight: 1.4,
        keywords: [
            "content curators",
            "curator list",
            "where are curators",
            "create content curator",
        ],
        steps: [
            "Open Users → Content Curators.",
            "Create via Create CC when needed.",
            "Open a curator profile; assign films from Box Office film actions.",
        ],
        route: '/admin/users/content-curators',
        linkLabel: "Open Content Curators",
    },
    {
        id: 'settings_pricing',
        title: "Pricing settings",
        audience: 'admin',
        rbacPage: RbacPage.SETTINGS_PRICING,
        weight: 1.8,
        keywords: [
            "pricing settings",
            "base price",
            "price chart",
            "sync price",
            "curator rating adjustments",
            "base max limit",
        ],
        steps: [
            "Open Settings → Financial → Pricing.",
            "Configure Base Max Limit, duration Base Price bands, curator adjustments, uplift rules.",
            "Preview Changes → Save → Sync Price. Optional Update filmmaker fees + notify.",
            "Export Price Chart when available.",
        ],
        route: '/admin/settings/financial',
        queryParams: { tab: 'pricing' },
        linkLabel: "Open Pricing Settings",
    },
    {
        id: 'settings_intl_pricing',
        title: "International pricing",
        audience: 'admin',
        rbacPage: RbacPage.SETTINGS_INTL_PRICING,
        weight: 1.7,
        keywords: [
            "international pricing",
            "usd exchange",
            "intl pricing",
            "foreign currency pricing",
        ],
        steps: [
            "Open Settings → Financial → International Pricing.",
            "Set USD exchange rate + multipliers.",
            "Update Price Setting, then Sync.",
        ],
        route: '/admin/settings/financial',
        queryParams: { tab: 'international-pricing' },
        linkLabel: "Open International Pricing",
    },
    {
        id: 'settings_operation',
        title: "Operation settings",
        audience: 'admin',
        rbacPage: RbacPage.SETTINGS_OPERATION,
        weight: 1.7,
        keywords: [
            "operation settings",
            "operation charges",
            "contest fees",
            "tax settings",
        ],
        steps: [
            "Open Settings → Financial → Operation Settings.",
            "Configure regular + contest operation charges, taxes, fees.",
            "Save, then Sync Films when required.",
        ],
        route: '/admin/settings/financial',
        queryParams: { tab: 'operation-settings' },
        linkLabel: "Open Operation Settings",
    },
    {
        id: 'settings_cashback',
        title: "Cashback settings",
        audience: 'admin',
        rbacPage: RbacPage.SETTINGS_CASHBACK,
        weight: 1.7,
        keywords: [
            "cashback settings",
            "android cashback",
            "ios cashback",
            "web cashback",
        ],
        steps: [
            "Open Settings → Rewards → Cashback.",
            "Configure per platform: Android / iOS / Web templates.",
            "Save each platform template carefully.",
        ],
        route: '/admin/settings/rewards',
        queryParams: { tab: 'cashback' },
        linkLabel: "Open Cashback Settings",
    },
    {
        id: 'settings_referral_event',
        title: "Referral event settings",
        audience: 'admin',
        rbacPage: RbacPage.SETTINGS_REFERRAL_EVENT,
        weight: 1.7,
        keywords: [
            "referral event settings",
            "referral codes",
            "referral program",
            "creator code",
        ],
        steps: [
            "Open Settings → Rewards → Referral Event Settings.",
            "Enable referral; set prefix/length, expiration, redemption, program copy.",
            "Configure role matrix rewards for referral + share-link events. Save.",
        ],
        route: '/admin/settings/rewards',
        queryParams: { tab: 'referral-event-settings' },
        linkLabel: "Open Referral Event Settings",
    },
    {
        id: 'engagement_campaigns',
        title: "Engagement campaigns",
        audience: 'admin',
        rbacPage: RbacPage.ENGAGEMENT_CAMPAIGN_LIST,
        weight: 1.5,
        keywords: [
            "engagement campaigns",
            "engagement campaign",
            "add campaign",
            "where campaigns",
        ],
        steps: [
            "Open Engagement Campaigns from the menu.",
            "Review list; Add Engagement Campaign to create.",
            "Open edit/view for an existing campaign.",
        ],
        route: '/admin/engagement-campaigns',
        linkLabel: "Open Engagement Campaigns",
    },
    {
        id: 'tbsc_create',
        title: "Create Big Short Challenge",
        audience: 'admin',
        rbacPage: RbacPage.CREATE_NEW_CHALLENGE,
        weight: 1.6,
        keywords: [
            "create challenge",
            "create big short",
            "new challenge",
            "tbsc create",
        ],
        steps: [
            "Open Big Short Challenge → Create New Challenge.",
            "Fill contest details and save.",
            "Manage entries under Challenges / Films / Filmmakers / Jury Films.",
        ],
        route: '/admin/big-short-challenge/create',
        linkLabel: "Create Challenge",
    },
    {
        id: 'tbsc_challenges',
        title: "Big Short Challenges list",
        audience: 'admin',
        rbacPage: RbacPage.CHALLENGE_LIST,
        weight: 1.5,
        keywords: [
            "challenges list",
            "tbsc challenges",
            "big short challenges",
            "contest list",
        ],
        steps: [
            "Open Big Short Challenge → Challenges.",
            "Open a contest to view/edit details.",
            "Use Films / Filmmakers / Jury Films for contest content.",
        ],
        route: '/admin/big-short-challenge/challenges',
        linkLabel: "Open Challenges",
    },
    {
        id: 'tbsc_films',
        title: "Big Short Challenge films",
        audience: 'admin',
        rbacPage: RbacPage.CHALLENGE_FILMS,
        weight: 1.5,
        keywords: [
            "challenge films",
            "tbsc films",
            "contest films",
            "big short films",
        ],
        steps: [
            "Open Big Short Challenge → Films.",
            "Review contest film submissions and statuses.",
        ],
        route: '/admin/big-short-challenge/films',
        linkLabel: "Open Challenge Films",
    },
    {
        id: 'tbsc_jury',
        title: "Jury films",
        audience: 'admin',
        rbacPage: RbacPage.JURY_FILMS_LIST,
        weight: 1.5,
        keywords: [
            "jury films",
            "jury list",
            "tbsc jury",
            "big short jury",
        ],
        steps: [
            "Open Big Short Challenge → Jury Films.",
            "Review jury-assigned titles and scoring workflow.",
        ],
        route: '/admin/big-short-challenge/jury-films',
        linkLabel: "Open Jury Films",
    },
    {
        id: 'vouchers',
        title: "Vouchers",
        audience: 'admin',
        rbacPage: RbacPage.VOUCHERS_LIST,
        weight: 1.5,
        keywords: [
            "vouchers",
            "voucher list",
            "add voucher",
            "where vouchers",
        ],
        steps: [
            "Open Vouchers → Vouchers List (or Add Voucher).",
            "Create/edit voucher codes and limits.",
            "Open a voucher row to view details.",
        ],
        route: '/admin/vouchers',
        linkLabel: "Open Vouchers",
    },
    {
        id: 'coupons',
        title: "Coupons",
        audience: 'admin',
        rbacPage: RbacPage.COUPONS_LIST,
        weight: 1.5,
        keywords: [
            "coupons",
            "coupon list",
            "add coupon",
            "where coupons",
        ],
        steps: [
            "Open Coupons → Coupons List (or Add Coupon).",
            "Create/edit coupons and redemption rules.",
            "Open a coupon for detail.",
        ],
        route: '/admin/coupons',
        linkLabel: "Open Coupons",
    },
    {
        id: 'qr_codes',
        title: "QR codes",
        audience: 'admin',
        rbacPage: RbacPage.QR_CODE_LIST,
        weight: 1.5,
        keywords: [
            "qr code",
            "qr codes",
            "create qr",
            "where qr code",
        ],
        steps: [
            "Open QR Code → QR Code List (or Create QR Code).",
            "Create/edit QR campaigns and destinations.",
            "Open a QR row to view/edit.",
        ],
        route: '/admin/qr-code',
        linkLabel: "Open QR Codes",
    },
    {
        id: 'audit_logs',
        title: "Audit Log",
        audience: 'admin',
        rbacPage: RbacPage.AUDIT_LOGS,
        weight: 1.5,
        keywords: [
            "audit log",
            "audit logs",
            "where audit log",
            "activity log",
            "who changed",
            "change history",
        ],
        steps: [
            "Open Logs → Audit Log.",
            "Filter by date/user/action to investigate changes.",
            "Use Error Log sibling page for runtime failures.",
        ],
        route: '/admin/audit-logs',
        linkLabel: "Open Audit Log",
        relatedPrompts: [
            "Where is error log?",
        ],
    },
    {
        id: 'error_logs',
        title: "Error Log",
        audience: 'admin',
        rbacPage: RbacPage.ERROR_LOGS,
        weight: 1.5,
        keywords: [
            "error log",
            "error logs",
            "where error log",
            "system errors",
        ],
        steps: [
            "Open Logs → Error Log.",
            "Filter recent failures and open a row for stack/context.",
            "Pair with Audit Log for who-changed-what.",
        ],
        route: '/admin/error-logs',
        linkLabel: "Open Error Log",
    },
    {
        id: 'languages_age_groups',
        title: "Languages & age groups",
        audience: 'admin',
        weight: 1.4,
        keywords: [
            "languages",
            "age groups",
            "add language",
            "add age group",
        ],
        steps: [
            "Open Languages or Age Groups from the top menu.",
            "Use form routes to add/edit master data used by uploads.",
        ],
        route: '/admin/languages',
        linkLabel: "Open Languages",
    },
    {
        id: 'upload_status_fm',
        title: "Film upload status",
        audience: 'filmmaker',
        weight: 1.5,
        keywords: [
            "upload status",
            "processing status",
            "film processing",
            "where upload status",
        ],
        steps: [
            "After Media Upload, open Film Upload Status if processing is pending.",
            "Wait for media processing to finish before final submit when required.",
            "Track finished titles under My Films.",
        ],
        route: '/film-maker/film-upload-status',
        linkLabel: "Open Upload Status",
    },
    {
        id: 'change_ui_language',
        title: 'Change Studio language',
        audience: 'both',
        weight: 1.55,
        keywords: [
            'change language',
            'how to change language',
            'switch language',
            'app language',
            'available app language',
            'available languages',
            'what languages',
            'studio language',
            'ui language',
        ],
        steps: [
            'Look at the top header of Studio for the language control (globe / language name).',
            'Open it and pick a language from the list (for example English, Hindi, Tamil, Telugu, Kannada, Malayalam when enabled).',
            'Studio text updates to that language. Ask Talkie questions in simple English for best results.',
        ],
        route: '/film-maker/dashboard',
        linkLabel: 'Open Dashboard',
        relatedPrompts: [
            'What are the available app languages?',
            'How do I upload a film?',
        ],
    },
    {
        id: 'edit_film_details_fm',
        title: 'Edit a film / movie details',
        audience: 'both',
        weight: 1.6,
        keywords: [
            'edit a movie',
            'edit movie details',
            'edit movies details',
            'edit movie\'s details',
            'edit film details',
            'how to edit a movie',
            'how to edit movies',
            'how to edit film',
            'how to edit movies details',
            'change film details',
            'update movie details',
            'edit my film',
            'edit movies',
            'movie details',
        ],
        steps: [
            'Open Films (My Films for filmmakers, or Box Office → Films for admins).',
            'Find the title and open it in edit mode (or ask Talkie: Open film "Title" in edit mode).',
            'Update the fields you need (title, language, genre, synopsis, media, pricing) and save.',
            'If the film is already approved, some fields may be locked — contact support if you need a change.',
        ],
        route: '/film-maker/my-films',
        linkLabel: 'Open films',
        relatedPrompts: [
            'Show my films',
            'Open film "Movie Name" in edit mode',
            'Help and support',
        ],
    },
    {
        id: 'change_film_banner_fm',
        title: 'Change movie banner / poster',
        audience: 'both',
        weight: 1.6,
        keywords: [
            'change banner',
            'change banner of a movie',
            'how to change banner',
            'how to change banner of a movie',
            'update banner',
            'change poster',
            'change thumbnail',
            'movie banner',
            'film banner',
            'upload banner',
            'banner of a movie',
        ],
        steps: [
            'Open the movie in edit mode from Films / My Films.',
            'Go to the media / images step (banner, poster, or thumbnail fields).',
            'Upload the new image (follow size / format hints on the form) and save.',
            'Home-page promo banners for the consumer app are managed under Home Page settings (admins).',
        ],
        route: '/film-maker/my-films',
        linkLabel: 'Open films',
        relatedPrompts: [
            'How to edit movies details',
            'Show my films',
        ],
    },
    {
        id: 'general_help',
        title: 'What Talkie can do',
        audience: 'both',
        keywords: [
            'help',
            'what can you do',
            'hello',
            'hi',
            'hey',
            'what can slate',
            'what can talkie',
            'ask talkie',
        ],
        steps: [
            'Ask live metrics: "Film views last 7 days" or "views on 10th August 2026".',
            'Ask lists: "Show my films", "List my all web series", "Approved web series".',
            'Ask how-to: "How do I upload a film?", "How to edit a movie details?", "How to change language?".',
            'Ask wallet / referral: "Tell me my current wallet balance", "What is my referral code?".',
            'Need a person? Ask "help and support" for Email, WhatsApp, and Call.',
        ],
        route: '/admin/dashboard',
        linkLabel: 'Open Dashboard',
        relatedPrompts: [
            'Film views last 7 days',
            'Help and support',
            'How do I upload a film?',
        ],
    },
    {
        id: 'approve_reject_film',
        title: 'Approve or reject a film',
        audience: 'admin',
        weight: 1.55,
        keywords: [
            'approve film',
            'reject film',
            'how to approve',
            'how to reject',
            'approve a movie',
            'reject a movie',
            'review film approval',
            'should i approve',
            'decline film',
        ],
        steps: [
            'Open Box Office → Films, or ask: Which movies are ready for approve?',
            'Conversion must be COMPLETE before Approve appears (ask: How to start film conversion?).',
            'Open the title in view/edit mode.',
            'Use Approve / Decline on the film (RBAC). Talkie cannot approve from chat.',
            'If buttons are missing, ask “What can I access?” or Help and support.',
        ],
        route: '/admin/box-office/films',
        linkLabel: 'Open Films list',
        relatedPrompts: [
            'Films in review',
            'Which movies are ready for approve?',
            'How to start film conversion?',
            'What can I access?',
            'Help and support',
        ],
    },
    {
        id: 'start_film_conversion',
        title: 'Start film conversion',
        audience: 'admin',
        weight: 1.55,
        keywords: [
            'start conversion',
            'convert film',
            'convert movie',
            'how to convert',
            'start film conversion',
            'video conversion',
            'how to start conversion',
        ],
        steps: [
            'Open Box Office → Films (or ask Talkie for the film).',
            'Open the title in view mode.',
            'Use Convert on the film row or Media section (RBAC: Convert permission).',
            'Wait until conversion status is COMPLETE before Approve is available.',
            'Talkie cannot start conversion from chat — Studio needs the confirm dialog.',
            'Stuck? Help and support.',
        ],
        route: '/admin/box-office/films',
        linkLabel: 'Open Films list',
        relatedPrompts: [
            'Which movies are ready for approve?',
            'How to approve a film?',
            'Help and support',
        ],
    },
    {
        id: 'studio_glossary',
        title: 'Studio wording (glossary)',
        audience: 'both',
        weight: 1.35,
        keywords: [
            'glossary',
            'box office means',
            'what is reel',
            'what is cash wallet',
            'tickets means purchases',
            'what is box office',
            'what does reel mean',
            'studio terms',
        ],
        steps: [
            'Tickets sold = purchases / sales count.',
            'Box office revenue = platform take; filmmaker revenue = your earnings (role-aware).',
            'Reel / cash = wallet balances on Filmmaker Studio.',
            'Ask live numbers with plain English, e.g. “tickets sold last month”.',
            'Numbers look wrong? Use Help and support with the exact ask + dates.',
        ],
        route: '/admin/dashboard',
        linkLabel: 'Open dashboard',
        relatedPrompts: [
            'Tickets sold last month',
            'Show revenue last 7 days',
            'Wallet balance',
            'Help and support',
        ],
    },
];

export function findRouteGuide(
    text: string,
    audience: 'admin' | 'filmmaker'
): SlateGuideEntry | null {
    const t = text.toLowerCase();
    let best: { guide: SlateGuideEntry; score: number } | null = null;

    for (const guide of SLATE_ROUTE_GUIDES) {
        if (guide.audience !== 'both' && guide.audience !== audience) continue;
        const score = scoreGuide(guide, t);
        if (score > 0 && (!best || score > best.score)) {
            best = { guide, score };
        }
    }
    return best?.guide ?? null;
}

/** Best guide across all roles — used to detect how-to that exists for another role */
export function findGuideAnyAudience(text: string): {
    guide: SlateGuideEntry | null;
    score: number;
} {
    const t = text.toLowerCase();
    let best: { guide: SlateGuideEntry; score: number } | null = null;
    for (const guide of SLATE_ROUTE_GUIDES) {
        const score = scoreGuide(guide, t);
        if (score > 0 && (!best || score > best.score)) {
            best = { guide, score };
        }
    }
    return { guide: best?.guide ?? null, score: best?.score ?? 0 };
}

function scoreGuide(guide: SlateGuideEntry, t: string): number {
    return scoreKeywordList(t, guide.keywords, guide.weight ?? 1);
}

export function resolveGuideRoute(
    guide: SlateGuideEntry,
    audience: 'admin' | 'filmmaker'
): string {
    if (guide.id === 'general_help' || guide.id === 'studio_glossary') {
        return audience === 'filmmaker'
            ? '/film-maker/dashboard'
            : '/admin/dashboard';
    }
    if (
        audience === 'admin' &&
        (guide.id === 'edit_film_details_fm' ||
            guide.id === 'change_film_banner_fm')
    ) {
        return '/admin/box-office/films';
    }
    return guide.route;
}

export function resolveGuideQueryParams(
    guide: SlateGuideEntry
): Record<string, string> | undefined {
    return guide.queryParams;
}
